import React from 'react';
import { Link } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { useAuth } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  BookOpen, 
  GraduationCap, 
  ScrollText, 
  Sparkles, 
  BookMarked,
  ChevronRight,
  Flame
} from 'lucide-react';

const quickActions = [
  {
    title: 'Explorar Escrituras',
    description: 'Navega por los cuatro volúmenes canónicos',
    icon: BookOpen,
    path: '/scriptures',
    color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400'
  },
  {
    title: 'Modo Seminario',
    description: 'Genera material para tus clases',
    icon: GraduationCap,
    path: '/seminary',
    color: 'bg-green-500/10 text-green-600 dark:text-green-400'
  },
  {
    title: 'Profundidad Doctrinal',
    description: 'Análisis temático profundo',
    icon: ScrollText,
    path: '/doctrinal',
    color: 'bg-purple-500/10 text-purple-600 dark:text-purple-400'
  },
  {
    title: 'Conexión Espiritual',
    description: 'Reflexiones guiadas con IA',
    icon: Sparkles,
    path: '/spiritual-connection',
    color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
  },
  {
    title: 'Mis Notas',
    description: 'Accede a tu repositorio personal',
    icon: BookMarked,
    path: '/notes',
    color: 'bg-rose-500/10 text-rose-600 dark:text-rose-400'
  }
];

const scriptures = [
  { name: 'La Biblia', books: 66, id: 'biblia' },
  { name: 'Libro de Mormón', books: 15, id: 'libro_de_mormon' },
  { name: 'Doctrina y Convenios', books: 1, id: 'doctrina_y_convenios' },
  { name: 'Perla de Gran Precio', books: 5, id: 'perla_de_gran_precio' }
];

const Dashboard = () => {
  const { user } = useAuth();

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Buenos días';
    if (hour < 18) return 'Buenas tardes';
    return 'Buenas noches';
  };

  return (
    <MainLayout>
      <div className="space-y-8 fade-in" data-testid="dashboard">
        {/* Welcome Section */}
        <div className="flex flex-col gap-2">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold">
            {getGreeting()}, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-muted-foreground">
            ¿Qué te gustaría estudiar hoy?
          </p>
        </div>

        {/* Quick Actions */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {quickActions.map((action) => (
            <Link key={action.path} to={action.path}>
              <Card className="h-full hover:shadow-lg transition-all hover:-translate-y-1 cursor-pointer group">
                <CardHeader className="pb-3">
                  <div className={`w-12 h-12 rounded-lg ${action.color} flex items-center justify-center mb-2 group-hover:scale-110 transition-transform`}>
                    <action.icon className="h-6 w-6" />
                  </div>
                  <CardTitle className="text-lg font-heading">{action.title}</CardTitle>
                  <CardDescription>{action.description}</CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <Button variant="ghost" size="sm" className="gap-1 p-0 h-auto text-primary">
                    Acceder <ChevronRight className="h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Scripture Volumes */}
        <div>
          <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
            <Flame className="h-5 w-5 text-primary" />
            Volúmenes de las Escrituras
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {scriptures.map((scripture) => (
              <Link key={scripture.id} to={`/scriptures?volume=${scripture.id}`}>
                <Card className="hover:shadow-md transition-shadow cursor-pointer">
                  <CardContent className="pt-6">
                    <h3 className="font-heading font-semibold mb-1">{scripture.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {scripture.books} {scripture.books === 1 ? 'libro' : 'libros'}
                    </p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>

        {/* Daily Inspiration */}
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                <Sparkles className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-heading font-semibold mb-2">Reflexión del día</h3>
                <blockquote className="text-muted-foreground italic scripture-text">
                  "Escudriñad las escrituras; porque a vosotros os parece que en ellas tenéis la vida eterna; y ellas son las que dan testimonio de mí."
                </blockquote>
                <p className="text-sm text-primary mt-2 font-medium">— Juan 5:39</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default Dashboard;
